import output from "_src/engine/output";
import intersectEvaluator from "_src/engine/intersect_evaluator";

import { vec3, vec4, mat4 } from "_src/libs/gl-matrix";

import config from "_src/config";
import {v1 as uuidv1} from "uuid";
import dxt from "dxt-js"
import {CompressedTextureFormat} from "_src/engine/constants";

export const colorUtils = {

  hexToRgb(hex) {

    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

    return result ? {
        r: parseInt(result[1], 16) / 255,
        g: parseInt(result[2], 16) / 255,
        b: parseInt(result[3], 16) / 255
    } : null;

  },

  rgbToHex(r, g, b) {
    return "#" + componentToHex(r) + componentToHex(g) + componentToHex(b);
  },

  linToSRGB(color, dest) {

    const exp = 1.0 / 2.2;

    dest[0] = Math.pow(color[0], exp);
    dest[1] = Math.pow(color[1], exp);
    dest[2] = Math.pow(color[2], exp);

    return dest;

  },

  srgbToLin(color, dest) {

    const exp = 2.2;

    dest[0] = Math.pow(color[0], exp);
    dest[1] = Math.pow(color[1], exp);
    dest[2] = Math.pow(color[2], exp);

    return dest;

  },

  generateHexColor() {

    const letters = "0123456789ABCDEF";

    let color = "#";
    for (let i = 0; i < 6; ++i) {
      color += letters[Math.floor(Math.random() * 16)];
    }

    return color;

  },

  flipImageBufferVertically(buffer, width, height) {

    const bytesPerRow = width * 4;
    const temp = new Uint8Array(bytesPerRow);

    for (let y = 0; y < height / 2; ++y) {

      const topOffset = y * bytesPerRow;
      const bottomOffset = (height - y - 1) * bytesPerRow;

      temp.set(buffer.subarray(topOffset, topOffset + bytesPerRow));

      buffer.copyWithin(topOffset, bottomOffset, bottomOffset + bytesPerRow);

      buffer.set(temp, bottomOffset);

    }

  },

  floatToByte(channel) {
    return Math.round(255 * channel);
  },

  getErrorColor(error, scanner, dest) {
    let greenThresh, yellowThresh, orangeThresh;

    switch (scanner) {
      case "Eva":
        greenThresh = 0.3;
        yellowThresh = 0.6;
        orangeThresh = 0.9;
        break;
      case "Spider":
        greenThresh = 0.1;
        yellowThresh = 0.2;
        orangeThresh = 0.3;
        break;
      case "Leo":
        greenThresh = 0.5;
        yellowThresh = 0.9;
        orangeThresh = 1.3;
        break;
      case "Ray":
        greenThresh = 0.9;
        yellowThresh = 1.9;
        orangeThresh = 2.9;
        break;
      case "Micro":
        greenThresh = 0.0;
        yellowThresh = 0.0;
        orangeThresh = 0.1;
        break;
      default:
        greenThresh = 0.3;
        yellowThresh = 0.6;
        orangeThresh = 0.9;
        break;
    }

    let originalColor;
    if (error === -2.0) {
      originalColor = config.darkRedErrorColor;
    } else if (error < greenThresh) {
      originalColor = config.greenErrorColor;
    } else if (error < yellowThresh) {
      originalColor = config.yellowErrorColor;
    } else if (error < orangeThresh) {
      originalColor = config.orangeErrorColor;
    } else {
      originalColor = config.redErrorColor;
    }

    //this matrix was calculated as an alignment between 5 pairs of hsl colors
    const correctionMatrix = mat4.fromValues(
        0.9874383520783551, -0.12860785277408343, -0.09179063704769921, 0,
        0.07208146412025583, 0.8836199441521241, -0.4626230180469598, 0,
        0.1406049905766614, 0.4501953070628759, 0.8817904638424597, 0,
        -14.590084244855852, -41.98817902010454, 61.52424687443911, 1);

    const originalColorHSL = this.rgbToHsl({r:originalColor[0], g: originalColor[1], b: originalColor[2]});
    const originalColorVector = vec3.fromValues(originalColorHSL.h, originalColorHSL.s, originalColorHSL.l);
    vec3.mul(originalColorVector, originalColorVector, [360, 100, 100]);
    const colorCorrectedVector = vec3.transformMat4(vec3.create(), originalColorVector, correctionMatrix);
    vec3.div(colorCorrectedVector, colorCorrectedVector, [360, 100, 100]);
    const colorCorrectedRgb = this.hslToRgb({h: colorCorrectedVector[0], s: colorCorrectedVector[1], l: colorCorrectedVector[2]});
    vec3.copy(dest, colorCorrectedRgb);

    return dest;

  },

  hueTorgb(p, q, t) {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  },

  hslToRgb ({ h, s, l }) {
    let r; let g; let b;
    if (s === 0) {
      r = g = b = l; // achromatic
    } else {
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      r = this.hueTorgb(p, q, h + 1 / 3);
      g = this.hueTorgb(p, q, h);
      b = this.hueTorgb(p, q, h - 1 / 3);
    }
    return [r, g, b];
  },

  rgbToHsl({r, g, b}) {
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = (max + min) / 2, s = h, l = h;

    if(max == min){
      h = s = 0; // achromatic
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch(max){
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }

    return { h, s, l };
  },

  generateColorInSequence(iteration, initialHue = 0, s = 0.5, l = 0.5) {
    const goldenRatioConjugate = 0.618033988749895;
    const currentColor = (initialHue + goldenRatioConjugate * iteration) % 1;
    const color = this.hslToRgb({ h: currentColor, s, l });
    return color;
  },

  getPaletteColorInSequence(iteration) {
    const index = Math.min(Math.round(halton(2, iteration) * paletteFull.length), paletteFull.length - 1);
    return paletteFull[index];
  },

  getPaletteColorUnique(colorList) {
    const availableColors = paletteFull.filter((color) => colorList.indexOf(color) === -1);
    if (availableColors.length) {
      return availableColors[Math.floor(Math.random() * availableColors.length)];
    } else {
      return paletteFull[Math.floor(Math.random() * paletteFull.length)];
    }
  },

  getPaletteColorRandom() {
    return paletteFull[Math.floor(Math.random() * paletteFull.length)];
  },

};

export const pathUtils = {

  getFileNameFromPath(path) {
    const parts = path.split("/");
    return parts[parts.length - 1];
  },

  getProjectNameFromPath(path) {
    const parts = path.split("/");
    return parts[parts.length - 2];
  },

  join(path1, path2) {
    return path1 + "/" + path2;
  }

}

export const idGenerator = {

  _IDs: new Set(),

  getNext() {

    const idBase = "n";
    let newID = "n00";
    let i = 0;

    while (this._IDs.has(newID)) {
      newID = idBase + ((i < 10) ? "0" + i: i);
      ++i;
    }

    this._IDs.add(newID);

    return newID;

  },

  initialize(json) {

    this._IDs.clear();

    const fusions = json["fusions"] || [];
    const scans = json["scans"] || [];
    const cads = json["cad-objects"] || [];
    const pointClouds = json["point-clouds"] || [];
    const groups = json["groups"] || [];

    for (const object of fusions.concat(scans).concat(cads).concat(pointClouds).concat(groups)) {
      const id = object["id"];
      if (id) {
        this._IDs.add(id);
      }
    }
  },

  generateUUID() {
    return uuidv1();
  },

};

export function decodeText(array) {
  if ( typeof TextDecoder !== "undefined" ) {
    return new TextDecoder().decode( array );
  }

  // Avoid the String.fromCharCode.apply(null, array) shortcut, which
  // throws a "maximum call stack size exceeded" error for large arrays.

  let s = "";
  for ( let i = 0, il = array.length; i < il; i ++ ) {
    // Implicitly assumes little-endian.
    s += String.fromCharCode( array[ i ] );
  }

  try {
    // merges multi-byte utf-8 characters.
    return decodeURIComponent( escape( s ) );
  } catch ( e ) { // see #16358
    return s;
  }
}

export function arrayToString(array) {
  let result = "";
  for (const number of array) {
    if (number === 0) {
      break;
    }
    result += String.fromCharCode(number);
  }
  return result;
}

export function uint32ToUint8Array(uint32) {
  const tmp = new Uint32Array(1);
  tmp[0] = uint32;
  return new Uint8Array(tmp.buffer);
}

export function arrayDataToNumber(array, offset, count) {
  const arrayBuffer = new Uint8Array(array.slice(offset, offset + count)).buffer;
  let typedBuffer;
  switch (count) {
    case 2:
      typedBuffer = new Uint16Array(arrayBuffer);
      break;
    case 4:
      typedBuffer = new Uint32Array(arrayBuffer);
      break;
    default:
      output.error(output.DEVELOPERS_ERROR, "Unsupported arrayDataToNumber request");
      return 0;
  }

  return typedBuffer[0];
}

export function isDataZipped(array) {
  const signature = arrayDataToNumber(array, 0, 4);
  return signature === 67324752 //0x80 0x75 0x3 0x4 - zip signature
}

export function getTimeStamp() {
  const dateObj = new Date();

  let hours = dateObj.getHours();
  if (hours < 10) { hours = "0" + hours; }

  let minutes = dateObj.getMinutes();
  if (minutes < 10) { minutes = "0" + minutes; }

  let seconds = dateObj.getSeconds();
  if (seconds < 10) { seconds = "0" + seconds; }

  return hours + ":" + minutes + ":" + seconds;
}

function componentToHex (channel) {

  const channelByte = Math.round(channel * 255);
  let hex = Number(channelByte).toString(16);
  if (hex.length < 2) {
    hex = "0" + hex;
  }

  return hex.toUpperCase();

}

export const getPositionByMinmax = (function() {

  const rayNW = vec3.create();
  const rayNE = vec3.create();
  const raySW = vec3.create();
  const raySE = vec3.create();

  const normalTop = vec4.create();
  const normalBottom = vec4.create();
  const normalLeft = vec4.create();
  const normalRight = vec4.create();

  const vec4Tmp = vec4.create();
  const vec4Tmp2 = vec4.create();

  const vec3Tmp = vec3.create();
  const vec3Tmp2 = vec3.create();

  return function(rotation, fov, aspect, minmax, rect, target, out) {

    calculateRayPersp(rect.minx, rect.maxy, fov, aspect, rotation, rayNW);
    calculateRayPersp(rect.maxx, rect.maxy, fov, aspect, rotation, rayNE);
    calculateRayPersp(rect.minx, rect.miny, fov, aspect, rotation, raySW);
    calculateRayPersp(rect.maxx, rect.miny, fov, aspect, rotation, raySE);

    vec3.cross(normalBottom, raySE, raySW);
    vec3.cross(normalTop, rayNW, rayNE);
    vec3.cross(normalRight, rayNE, raySE);
    vec3.cross(normalLeft, raySW, rayNW);

    calculateRayPersp(0.5 * (rect.minx + rect.maxx), rect.maxy, fov, aspect, rotation, vec3Tmp);
    calculateRayPersp(0.5 * (rect.minx + rect.maxx), rect.miny, fov, aspect, rotation, vec3Tmp2);
    const planeMiddleVert = vec3.cross(vec4Tmp, vec3Tmp, vec3Tmp2);
    planeMiddleVert[3] = -vec3.dot(planeMiddleVert, target);

    calculateRayPersp(rect.minx, 0.5 * (rect.maxy + rect.miny), fov, aspect, rotation, vec3Tmp);
    calculateRayPersp(rect.maxx, 0.5 * (rect.maxy + rect.miny), fov, aspect, rotation, vec3Tmp2);
    const planeMiddleHor = vec3.cross(vec4Tmp2, vec3Tmp, vec3Tmp2);
    planeMiddleHor[3] = -vec3.dot(planeMiddleHor, target);

    const planeLeft = calcBoundingPlane(normalLeft, minmax, normalLeft);
    const planeRight = calcBoundingPlane(normalRight, minmax, normalRight);
    const planeTop = calcBoundingPlane(normalTop, minmax, normalTop);
    const planeBottom = calcBoundingPlane(normalBottom, minmax, normalBottom);

    const point1 = intersectEvaluator.calcThreePlanesIntersection(planeMiddleHor, planeLeft, planeRight, vec3Tmp);
    const point2 = intersectEvaluator.calcThreePlanesIntersection(planeMiddleVert, planeTop, planeBottom, vec3Tmp2);

    return vec3.dist(target, point1) > vec3.dist(target, point2) ? vec3.copy(out, point1) : vec3.copy(out, point2);

  }

})();

const calcBoundingPlane = (function() {

  const vec3Tmp = vec3.create();
  const vec3Tmp2 = vec3.create();

  return function(normal, minmax, out) {

    for (let i = 0; i < 8; ++i) {

      const x = minmax[    3 * ((i & 0b100) >> 2)];
      const y = minmax[1 + 3 * ((i & 0b010) >> 1)];
      const z = minmax[2 + 3 * ((i & 0b001))];

      const vertex = vec3.set(vec3Tmp, x, y, z);
      let positiveDistVerticesCounter = 0;

      for (let j = 0; j < 8; ++j) {

        if (i == j) {
          continue;
        }

        const x = minmax[    3 * ((j & 0b100) >> 2)];
        const y = minmax[1 + 3 * ((j & 0b010) >> 1)];
        const z = minmax[2 + 3 * ((j & 0b001))];

        const point = vec3.set(vec3Tmp2, x, y, z);

        const distToPlane = vec3.dot(point, normal) - vec3.dot(vertex, normal);

        if (distToPlane >= 0) {
          ++positiveDistVerticesCounter;
        }

      }

      if (positiveDistVerticesCounter == 7) {
        vec3.copy(out, normal);
        out[3] = -vec3.dot(normal, vertex);
        return out;
      }

    }

    return null;

  }

})();

export const calcOrthoCameraFrustum = (function() {

  const vec4Tmp = vec4.create();
  const vec4Tmp2 = vec4.create();
  const vec4Tmp3 = vec4.create();
  const vec4Tmp4 = vec4.create();

  return function(rotation, minmax, rect) {

    const rightNormal = vec3.set(vec4Tmp, -1, 0, 0);
    const leftNormal = vec3.set(vec4Tmp2, 1, 0, 0);
    const topNormal = vec3.set(vec4Tmp3, 0, -1, 0);
    const bottomNormal = vec3.set(vec4Tmp4, 0, 1, 0);

    vec3.transformQuat(rightNormal, rightNormal, rotation);
    vec3.transformQuat(leftNormal, leftNormal, rotation);
    vec3.transformQuat(topNormal, topNormal, rotation);
    vec3.transformQuat(bottomNormal, bottomNormal, rotation);

    const planeRight = calcBoundingPlane(rightNormal, minmax, rightNormal);
    const planeLeft = calcBoundingPlane(leftNormal, minmax, leftNormal);
    const planeTop = calcBoundingPlane(topNormal, minmax, topNormal);
    const planeBottom = calcBoundingPlane(bottomNormal, minmax, bottomNormal);

    vec4.scale(planeLeft, planeLeft, -1);
    vec4.scale(planeBottom, planeBottom, -1);

    let height = 0.5 * Math.abs(planeTop[3] - planeBottom[3]);
    let width = 0.5 * Math.abs(planeRight[3] - planeLeft[3]);

    height += height * (1.0 - Math.min(Math.abs(rect.maxy), Math.abs(rect.miny)));
    width += width * (1.0 - Math.min(Math.abs(rect.maxx), Math.abs(rect.minx)));

    return { height, width };

  }

})();

const calculateRayPersp = (function() {

  const vec3Tmp = vec3.create();

  return function(x, y, fov, aspect, rotation, dest) {

    const tanFOV = Math.tan(fov * 0.5);

    const locX = x * tanFOV * aspect;
    const locY = y * tanFOV;

    const ray = vec3.set(vec3Tmp, locX, locY, -1);
    vec3.transformQuat(ray, ray, rotation);

    return vec3.normalize(dest, ray);

  }

})();

export const calcOrthoCameraProjection = (function() {

  const vec3Tmp = vec4.create();

  return function(camera, target) {

    const view = vec3.set(vec3Tmp, 0, 0, -1);
    vec3.transformQuat(view, view, camera.quat);

    const dist = Math.abs(vec3.dot(view, camera.trans) - vec3.dot(target, view));

    const height = Math.tan(0.5 * camera.fov) * dist;
    const width = height * camera.aspect;

    return { height, width };

  }

})();

function halton(b, j) {
  let r = 0.0;
  let f = 1.0;
  let i = j;
  while (i > 0) {
    f = f / b;
    r = r + f * (i % b);
    i = Math.floor(i / b);
  }
  return r;
}

export function readDDs(bufferIn, imageOut) {
  const HEADER_SIZE = 31 * 4;

  const OFF_MAGIC   = 0 * 4;
  const OFF_SIZE    = 1 * 4;
  const OFF_HEIGHT  = 3 * 4;
  const OFF_WIDTH   = 4 * 4;
  const OFF_FOURCC  = 21 * 4;

  const DDS_MAGIC   = 0x20534444;
  const FOURCC_DXT1 = 827611204;
  const FOURCC_DXT3 = 861165636;
  const FOURCC_DXT5 = 894720068;

  if (bufferIn.byteLength < HEADER_SIZE) return false;

  const dataView = new DataView(bufferIn);
  if (dataView.getInt32(OFF_MAGIC, true) !== DDS_MAGIC) return false;

  const fourCC = dataView.getInt32(OFF_FOURCC, true);
  let blockSize;
  switch (fourCC) {
    case FOURCC_DXT1:
      blockSize = 8;
      break
    case FOURCC_DXT3:
      blockSize = 16;
      break
    case FOURCC_DXT5:
      blockSize = 16;
      break
    default:
      return false;
  }

  const width = dataView.getInt32(OFF_WIDTH, true);
  const height = dataView.getInt32(OFF_HEIGHT, true);
  imageOut.width = width;
  imageOut.height = height;

  const dataOffset = dataView.getInt32(OFF_SIZE, true) + 4;
  const dataLength = Math.max( 4, width ) / 4 * Math.max( 4, height ) / 4 * blockSize;
  imageOut.buffer = new Uint8Array(bufferIn, dataOffset, dataLength);

  return true;
}

export function unCompressDxt(buffer, width, height, format) {
  let libInternalFormat;
  switch (format) {
    case CompressedTextureFormat.DXT1:
      libInternalFormat = dxt.flags.DXT1;
      break;
    case CompressedTextureFormat.DXT3:
      libInternalFormat = dxt.flags.DXT3;
      break;
    case CompressedTextureFormat.DXT5:
      libInternalFormat = dxt.flags.DXT5;
      break;
  }

  const uncompressed = dxt.decompress(buffer, width, height, libInternalFormat);
  return uncompressed;
}

const paletteFull = [
"#999A67",
"#9A9922",
"#5022F3",
"#2522B3",
"#FF6622",
"#CD6621",
"#2667B3",
"#4F66F3",
"#9ACC3F",
"#44CD3E",
"#670001",
"#673334",
"#FF2367",
"#E66767",
"#FFFFFF",
"#CCCCCD",
"#CD9967",
"#CD9922",
"#CD2367",
"#9A2367",
"#343334",
"#FFFF5B",
"#CEFF5B",
"#672267",
"#81239A",
"#FFFFCD",
"#CDCC9A",
"#CDFFFF",
"#61FFFF",
"#CD239A",
"#CD2320",
"#FF9A67",
"#FF9922",
"#9A2334",
"#E79AFF",
"#E69ACD",
"#220067",
"#9A67E6",
"#B523E6",
"#419A66",
"#419A22",
"#CDFFCD",
"#62FFB3",
"#CDCCFF",
"#FFCDFF",
"#670034",
"#013301",
"#333400",
"#FFCCCD",
"#E69A9A",
"#60CDCC",
"#61FF40",
"#673301",
"#9A2301",
"#CDCC3F",
"#67669A",
"#9A669A",
"#5FCD99",
"#FFCD99",
"#FFCC3F",
"#F34E9A",
"#233467",
"#003434",
"#236766",
"#676620",
"#246720",
"#F44EE6",
"#449AE6",
"#60CDFF",
"#9A9AE6",
"#676767",
"#996767",
"#999A9A",
"#429A99",
"#9A6621"
];
